import React, { useState, useEffect } from "react";
import { FBProfile } from "~/.server/api";
import { cn } from "~/lib/utils";
import CreditIconSVG from "~/components/credit-icon-svg";
import CreditsButton from "~/components/credits-button";
import { FaCheck, FaLock, FaHourglassHalf } from "react-icons/fa";
import { CgSpinner } from "react-icons/cg";
import { IoMdClose } from "react-icons/io";
import Button from "~/components/button";
import {
  getDailyRewardBlockReason,
  isFreeBonusWindowActive,
} from "~/lib/daily-reward-policy";

type DailyRewardData = FBProfile["metadata"]["dailyReward"];

interface DailyRewardModalProps {
  dailyRewardData: DailyRewardData;
  onClose: () => void;
  onClaim: (claimIndex: number) => void;
  userCredits: number;
  purchasesCount: number;
}

// Progress Node Component for the new UI - now visual only
interface ProgressNodeProps {
  index: number; // 0, 1, or 2
  claimsMade: number;
  lastClaimTimestamp: string | null | undefined;
  intervalsSeconds: number[] | undefined;
  claimCredits: number[] | undefined;
}

// Helper function to format remaining seconds into HH:MM:SS
const formatTime = (totalSeconds: number): string => {
  if (totalSeconds <= 0) return "00:00:00";
  totalSeconds = Math.floor(totalSeconds);

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(2, "0")}`;
};

// Progress Fill Component with proper timing logic
interface ProgressFillProps {
  claimsMade: number;
  lastClaimTimestamp: string | null | undefined;
  intervals: number[] | undefined;
  cycleCooldownUntilTimestamp: string | null | undefined;
}

const ProgressFill: React.FC<ProgressFillProps> = ({
  claimsMade,
  lastClaimTimestamp,
  intervals,
  cycleCooldownUntilTimestamp,
}) => {
  const [progressPercent, setProgressPercent] = useState<number>(0);

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const updateProgress = () => {
      const nodePositions = [20, 50, 80];
      const nodeRadiusPercent = 2;

      const segments = [
        { start: 0, end: nodePositions[0] - nodeRadiusPercent },
        { start: nodePositions[0] + nodeRadiusPercent, end: nodePositions[1] - nodeRadiusPercent },
        { start: nodePositions[1] + nodeRadiusPercent, end: nodePositions[2] - nodeRadiusPercent },
        { start: nodePositions[2] + nodeRadiusPercent, end: 100 }
      ];

      const lastClaimTime = lastClaimTimestamp
        ? new Date(lastClaimTimestamp).getTime()
        : null;
      const cycleCooldownEndTime = cycleCooldownUntilTimestamp
        ? new Date(cycleCooldownUntilTimestamp).getTime()
        : null;
      const intervalsArray = intervals ?? [300, 3600, 28800, 86400];

      let currentSegmentIndex: number;
      let targetTimestamp: number | null = null;
      let maxDuration: number = 1;

      if (claimsMade >= 3) {
        currentSegmentIndex = 3;
        if (cycleCooldownEndTime && cycleCooldownEndTime > Date.now()) {
          targetTimestamp = cycleCooldownEndTime;
          maxDuration = intervalsArray[3];
        } else {
          setProgressPercent(segments[3].end);
          return;
        }
      } else {
        currentSegmentIndex = claimsMade;

        if (!lastClaimTime) {
          setProgressPercent(segments[0].start);
          return;
        } else {
          const cooldownSeconds = intervalsArray[claimsMade] ?? 30;
          targetTimestamp = lastClaimTime + cooldownSeconds * 1000;
          maxDuration = cooldownSeconds;
        }
      }

      if (targetTimestamp && maxDuration > 0) {
        const now = Date.now();
        const startTime = targetTimestamp - maxDuration * 1000;
        const currentSegment = segments[currentSegmentIndex];

        if (now >= targetTimestamp) {
          setProgressPercent(currentSegment.end);
        } else {
          const timeElapsed = Math.max(0, now - startTime);
          const timeProgress = Math.min(1, Math.max(0, timeElapsed / (maxDuration * 1000)));
          const segmentWidth = currentSegment.end - currentSegment.start;
          const currentProgress = currentSegment.start + (timeProgress * segmentWidth);
          setProgressPercent(currentProgress);
        }
      }
    };

    updateProgress();
    intervalId = setInterval(updateProgress, 1000);

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [claimsMade, lastClaimTimestamp, intervals, cycleCooldownUntilTimestamp]);

  return (
    <div
      className="absolute inset-y-0 left-0 bg-gradient-to-r from-red-600 via-red-500 to-red-700 rounded-l-full transition-all duration-1000 shadow-[0_0_20px_rgba(255,0,0,0.5)] animate-[gradientShift_3s_ease_infinite]"
      style={{
        width: `${Math.max(progressPercent, 0)}%`,
        borderTopRightRadius: progressPercent >= 100 ? '9999px' : '0px',
        borderBottomRightRadius: progressPercent >= 100 ? '9999px' : '0px',
        backgroundSize: '200% 100%'
      }}
    />
  );
};

const ProgressNode: React.FC<ProgressNodeProps> = ({
  index,
  claimsMade,
  lastClaimTimestamp,
  intervalsSeconds,
}) => {
  const [remainingCooldown, setRemainingCooldown] = useState<number | null>(
    null
  );
  const [isActuallyClaimable, setIsActuallyClaimable] = useState(false);

  const isStructurallyClaimable = index === claimsMade;
  const isClaimed = index < claimsMade;
  const isLockedByCount = index > claimsMade;

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    setIsActuallyClaimable(false);
    setRemainingCooldown(null);

    if (isStructurallyClaimable && !isClaimed && !isLockedByCount) {
      const lastClaimTime = lastClaimTimestamp
        ? new Date(lastClaimTimestamp).getTime()
        : null;
      const cooldownSeconds =
        (intervalsSeconds && intervalsSeconds[claimsMade]) ?? 30;
      const targetTimestamp = lastClaimTime
        ? lastClaimTime + cooldownSeconds * 1000
        : Date.now();

      const checkCooldown = () => {
        const now = Date.now();
        if (now >= targetTimestamp) {
          setIsActuallyClaimable(true);
          setRemainingCooldown(0);
          if (intervalId) clearInterval(intervalId);
        } else {
          setIsActuallyClaimable(false);
          const remaining = Math.max(0, (targetTimestamp - now) / 1000);
          setRemainingCooldown(remaining);
        }
      };

      checkCooldown();
      if (Date.now() < targetTimestamp) {
        intervalId = setInterval(checkCooldown, 1000);
      }
    } else if (isClaimed || isLockedByCount) {
      setIsActuallyClaimable(false);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [
    index,
    claimsMade,
    lastClaimTimestamp,
    intervalsSeconds,
    isStructurallyClaimable,
    isClaimed,
    isLockedByCount,
  ]);

  let nodeClasses = "flex items-center justify-center transition-all duration-300";
  let iconContent;

  if (isClaimed) {
    nodeClasses += " rounded-full shadow-md border-2 w-8 h-8 bg-white border-yellow-400 shadow-[0_0_15px_rgba(255,215,0,0.5)]";
    iconContent = <FaCheck className="w-3 h-3 text-green-600" />;
  } else if (isStructurallyClaimable && !isActuallyClaimable && remainingCooldown !== null) {
    nodeClasses += " rounded-full shadow-md border-2 w-8 h-8 bg-gray-300 border-gray-400 dark:bg-gray-700 dark:border-gray-600";
    iconContent = <FaHourglassHalf className="w-3 h-3 text-primary dark:text-yellow-400 animate-spin" />;
  } else if (isStructurallyClaimable && isActuallyClaimable) {
    nodeClasses += " w-10 h-10 animate-[pulseNode_1s_infinite] drop-shadow-[0_4px_8px_rgba(255,215,0,0.5)]";
    iconContent = (
      <img
        src="/gift-svgrepo-com.svg"
        alt="Gift"
        className="w-full h-full filter drop-shadow-lg"
      />
    );
  } else {
    nodeClasses += " rounded-full shadow-md border-2 w-8 h-8 bg-gray-200 border-gray-300 dark:bg-gray-800 dark:border-gray-600 opacity-60";
    iconContent = <FaLock className="w-3 h-3 text-gray-500" />;
  }

  return (
    <div className={nodeClasses}>
      {iconContent}
    </div>
  );
};

// Claim/Timer Component
interface ClaimTimerProps {
  claimsMade: number;
  lastClaimTimestamp: string | null | undefined;
  intervals: number[] | undefined;
  isCycleCompleteAndCooling: boolean;
  onClaim: (index: number) => void;
  claimingIndex: number | null;
  claimCredits?: number[] | undefined;
}

const ClaimTimerComponent: React.FC<ClaimTimerProps> = ({
  claimsMade,
  lastClaimTimestamp,
  intervals,
  isCycleCompleteAndCooling,
  onClaim,
  claimingIndex,
  claimCredits,
}) => {
  const [currentCooldownRemaining, setCurrentCooldownRemaining] = useState<number | null>(null);

  const canClaim = !isCycleCompleteAndCooling && claimsMade < 3;
  const nextClaimIndex = claimsMade;

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    setCurrentCooldownRemaining(null);

    if (canClaim && lastClaimTimestamp && intervals) {
      const lastClaimTime = new Date(lastClaimTimestamp).getTime();
      const cooldownSeconds = intervals[claimsMade] ?? 30;
      const targetTimestamp = lastClaimTime + cooldownSeconds * 1000;

      const checkCooldown = () => {
        const now = Date.now();
        if (now >= targetTimestamp) {
          setCurrentCooldownRemaining(0);
          if (intervalId) clearInterval(intervalId);
        } else {
          const remaining = Math.max(0, (targetTimestamp - now) / 1000);
          setCurrentCooldownRemaining(remaining);
        }
      };

      checkCooldown();
      if (Date.now() < targetTimestamp) {
        intervalId = setInterval(checkCooldown, 1000);
      }
    } else if (claimsMade === 0 && !lastClaimTimestamp) {
      setCurrentCooldownRemaining(0);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [claimsMade, lastClaimTimestamp, intervals, canClaim]);

  const showClaimButton = canClaim && (currentCooldownRemaining === null || currentCooldownRemaining <= 0);
  const showTimer = canClaim && currentCooldownRemaining !== null && currentCooldownRemaining > 0;

  if (showClaimButton) {
    const isLoading = claimingIndex === nextClaimIndex;
    const creditAmount = claimCredits?.[nextClaimIndex] ?? 0;

    return (
      <div className="text-center mb-4">
        <button
          onClick={() => onClaim(nextClaimIndex)}
          disabled={isLoading}
          className="relative bg-gradient-to-r from-red-600 via-red-500 to-red-700 text-white border-2 border-yellow-400 rounded-full px-8 py-3 font-bold uppercase tracking-wider shadow-[0_6px_12px_rgba(0,0,0,0.4),inset_0_-2px_4px_rgba(0,0,0,0.3)] hover:from-yellow-400 hover:via-yellow-500 hover:to-yellow-600 hover:text-black hover:-translate-y-0.5 hover:shadow-[0_8px_16px_rgba(255,215,0,0.3)] active:scale-95 transition-all duration-300 overflow-hidden group animate-pulse disabled:opacity-50"
        >
          <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
          <span className="relative">
            {isLoading ? (
              <div className="flex items-center gap-2">
                <CgSpinner className="w-5 h-5 animate-spin" />
                <span>Claiming...</span>
              </div>
            ) : (
              `Claim ${creditAmount} Free Credits`
            )}
          </span>
        </button>
      </div>
    );
  }

  if (showTimer) {
    return (
      <div className="text-center mb-4">
        <div className="text-gray-500 dark:text-gray-400 text-sm mb-1 uppercase tracking-wider">
          Time until next reward:
        </div>
        <div className="inline-block bg-white border-2 border-border dark:border-red-500 dark:bg-gradient-to-br dark:from-gray-800 dark:to-black rounded-lg px-4 py-2">
          <div className="font-mono text-xl font-bold text-primary dark:text-yellow-400 text-shadow-[2px_2px_4px_rgba(0,0,0,0.5)]">
            {formatTime(currentCooldownRemaining)}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

// Main Modal Component
const DailyRewardModal: React.FC<DailyRewardModalProps> = ({
  dailyRewardData,
  onClose,
  onClaim,
  userCredits,
  purchasesCount,
}) => {
  const [claimingIndex, setClaimingIndex] = useState<number | null>(null);
  const [cycleCooldownRemaining, setCycleCooldownRemaining] = useState<
    number | null
  >(null);
  const [endDateRemaining, setEndDateRemaining] = useState<number | null>(null);

  const claimsMade = dailyRewardData?.claimsMadeThisCycle ?? 0;
  const lastClaimTimestamp = dailyRewardData?.lastClaimTimestamp;
  const cycleCooldownUntilTimestamp =
    dailyRewardData?.cycleCooldownUntilTimestamp;
  const intervals = dailyRewardData?.intervalsSeconds;
  const credits = dailyRewardData?.claimCredits;
  const isCycleCompleteAndCooling =
    claimsMade >= 3 &&
    !!cycleCooldownUntilTimestamp &&
    new Date(cycleCooldownUntilTimestamp) > new Date();

  // --- Blocked state computation using shared policy helper ---
  const blockReason = getDailyRewardBlockReason(
    dailyRewardData?.endDate,
    purchasesCount,
    userCredits,
  );
  const isBlocked = blockReason !== null;
  const isWindowExpired = !isFreeBonusWindowActive(dailyRewardData?.endDate);

  const handleCardClaim = (index: number) => {
    setClaimingIndex(index);
    onClaim(index);
  };

  // Reset claiming index if modal data changes
  useEffect(() => {
    setClaimingIndex(null);
  }, [dailyRewardData]);

  // Effect to calculate remaining cycle cooldown time
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    setCycleCooldownRemaining(null);

    if (isCycleCompleteAndCooling && cycleCooldownUntilTimestamp) {
      const targetTimestamp = new Date(cycleCooldownUntilTimestamp).getTime();

      const checkCycleCooldown = () => {
        const now = Date.now();
        if (now >= targetTimestamp) {
          setCycleCooldownRemaining(0);
          if (intervalId) clearInterval(intervalId);
        } else {
          setCycleCooldownRemaining(
            Math.max(0, (targetTimestamp - now) / 1000)
          );
        }
      };

      checkCycleCooldown();
      if (Date.now() < targetTimestamp) {
        intervalId = setInterval(checkCycleCooldown, 1000);
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isCycleCompleteAndCooling, cycleCooldownUntilTimestamp]);

  // Effect to calculate remaining endDate time
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    setEndDateRemaining(null);

    if (dailyRewardData?.endDate && !isWindowExpired) {
      const targetTimestamp = new Date(dailyRewardData.endDate).getTime();

      const checkEndDate = () => {
        const now = Date.now();
        if (now >= targetTimestamp) {
          setEndDateRemaining(0);
          if (intervalId) clearInterval(intervalId);
        } else {
          setEndDateRemaining(Math.max(0, (targetTimestamp - now) / 1000));
        }
      };

      checkEndDate();
      if (Date.now() < targetTimestamp) {
        intervalId = setInterval(checkEndDate, 1000);
      }
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [dailyRewardData?.endDate, isWindowExpired]);

  return (
    <div className="bg-componentBackground border-2 border-border dark:border-red-500 dark:bg-gradient-to-br dark:from-red-950 dark:via-black dark:to-red-900 rounded-3xl p-6 max-w-[500px] w-[90vw] md:w-full shadow-[0_0_50px_rgba(255,0,0,0.5),inset_0_0_30px_rgba(255,0,0,0.1),0_20px_60px_rgba(0,0,0,0.8)] relative overflow-hidden">
      {/* Close Button */}
      <Button
        variant="ghost"
        className="absolute top-3 right-3 p-2 hover:bg-primary w-15 h-10 rounded-full bg-gray-200 text-text1 dark:bg-black dark:text-white border-0"
        onClick={onClose}
        aria-label="Close"
      >
        <IoMdClose size={24} />
      </Button>

      {/* Floating coins in background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-16 left-8 opacity-15 animate-[floatGem_4s_infinite_ease-in-out] w-8 h-8">
          <img
            src="/gift-svgrepo-com.svg"
            alt="Coin"
            className="w-full h-full filter drop-shadow-lg"
          />
        </div>
        <div className="absolute top-10 right-16 opacity-15 animate-[floatGem_4s_infinite_ease-in-out] [animation-delay:1s] w-10 h-10">
          <img
            src="/gift-svgrepo-com.svg"
            alt="Coin"
            className="w-full h-full filter drop-shadow-lg"
          />
        </div>
        <div className="absolute bottom-16 right-10 opacity-15 animate-[floatGem_4s_infinite_ease-in-out] [animation-delay:2s] w-9 h-9">
          <img
            src="/gift-svgrepo-com.svg"
            alt="Coin"
            className="w-full h-full filter drop-shadow-lg"
          />
        </div>
      </div>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes floatGem {
          0%, 100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-10px) rotate(180deg);
          }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes pulseNode {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        `
      }} />

      {/* Content container */}
      <div className="relative">
        {/* Header */}
        <div className="text-center mb-8 mt-4">
          <h1 className="text-3xl md:text-4xl font-black text-primary dark:text-yellow-400 uppercase tracking-wider animate-pulse">
            Daily Rewards
          </h1>
        </div>

        {/* Progress system */}
        <div className="relative mb-6 mx-6">
          {/* Credit icons and amounts above the progress bar */}
          <div className="relative mb-2 h-14">
            {[0, 1, 2].map((index) => {
              const currentCreditReward = (credits && credits[index]) ?? 0;
              const isClaimed = index < claimsMade;
              const isActive = index === claimsMade;
              const nodePositions = ['20%', '50%', '80%'];

              return (
                <div
                  key={index}
                  className="absolute top-2 flex flex-col items-center transform -translate-x-1/2"
                  style={{ left: nodePositions[index] }}
                >
                  <div className={cn(
                    "transition-all duration-300 pointer-events-none",
                    isClaimed
                      ? "opacity-50 scale-125"
                      : isActive
                        ? "scale-150"
                        : "opacity-75 scale-125"
                  )}>
                    <CreditsButton credits={currentCreditReward} onClick={undefined} showPlus />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Main progress track */}
          <div className="relative h-9 bg-white border-2 border-border dark:border-red-500 dark:bg-gradient-to-br dark:from-gray-800 dark:to-black rounded-full shadow-[inset_0_2px_4px_rgba(0,0,0,0.5),0_4px_8px_rgba(0,0,0,0.3)] overflow-hidden">
            <ProgressFill
              claimsMade={claimsMade}
              lastClaimTimestamp={lastClaimTimestamp}
              intervals={intervals}
              cycleCooldownUntilTimestamp={cycleCooldownUntilTimestamp}
            />

            <div className="absolute inset-0 flex items-center">
              {[0, 1, 2].map((index) => {
                const nodePositions = ['20%', '50%', '80%'];

                return (
                  <div
                    key={index}
                    className="absolute transform -translate-x-1/2"
                    style={{ left: nodePositions[index] }}
                  >
                    <ProgressNode
                      index={index}
                      claimsMade={claimsMade}
                      lastClaimTimestamp={lastClaimTimestamp}
                      intervalsSeconds={intervals}
                      claimCredits={credits}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Claim/Timer OR Blocked Message */}
        {isBlocked ? (
          <div className="mx-4 mb-4">
            <div className="bg-white border-2 border-orange-400 dark:bg-gradient-to-br dark:from-gray-800 dark:to-black rounded-lg p-4 text-center">
              {blockReason.reasonCode === "WINDOW_EXPIRED" ? (
                <>
                  <div className="text-orange-600 dark:text-orange-400 font-bold text-lg mb-2">
                    Bonus Window Expired
                  </div>
                  <div className="text-text1 dark:text-gray-300 text-sm">
                    {blockReason.message}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-orange-600 dark:text-orange-400 font-bold text-lg mb-2">
                    Credit Threshold Reached
                  </div>
                  <div className="text-text1 dark:text-gray-300 text-sm">
                    {blockReason.message}
                  </div>
                </>
              )}
            </div>
          </div>
        ) : (
          <ClaimTimerComponent
            claimsMade={claimsMade}
            lastClaimTimestamp={lastClaimTimestamp}
            intervals={intervals}
            isCycleCompleteAndCooling={isCycleCompleteAndCooling}
            onClaim={handleCardClaim}
            claimingIndex={claimingIndex}
            claimCredits={credits}
          />
        )}

        {/* Status message */}
        {!isBlocked && isCycleCompleteAndCooling ? (
          <div className="mx-4 mb-4">
            <div className="bg-white border-2 border-green-500 dark:bg-gradient-to-br dark:from-gray-800 dark:to-black rounded-lg p-4 text-center">
              <div className="text-green-600 dark:text-green-400 font-bold text-lg mb-2">
                All rewards claimed for today! 🎉
              </div>
              <div className="text-text1 dark:text-gray-300 text-sm">
                Next cycle starts in{" "}
                <span className="font-mono text-primary dark:text-yellow-400 font-bold">
                  {cycleCooldownRemaining !== null
                    ? formatTime(cycleCooldownRemaining)
                    : "..."}
                </span>
              </div>
            </div>
          </div>
        ) : !isBlocked && claimsMade === 0 ? (
          <div className="text-center mx-4 mb-4">
            <div className="text-text1 dark:text-gray-300 text-sm">
              Collect your <span className="text-primary dark:text-yellow-400 font-bold">FREE CREDITS</span> when the timer completes!
            </div>
          </div>
        ) : !isBlocked && claimsMade < 3 ? (
          <div className="text-center mx-4 mb-4">
            <div className="text-text1 dark:text-gray-300 text-sm">
              Keep going! <span className="text-primary dark:text-yellow-400 font-bold">{3 - claimsMade} more {3 - claimsMade === 1 ? 'reward' : 'rewards'}</span> to claim today.
            </div>
          </div>
        ) : null}

        {/* EndDate countdown when window is active */}
        {!isWindowExpired && endDateRemaining !== null && endDateRemaining > 0 && (
          <div className="text-center mx-4 mb-2">
            <div className="text-gray-500 dark:text-gray-400 text-xs">
              Bonus window expires in{" "}
              <span className="font-mono text-primary dark:text-yellow-400 font-bold">
                {Math.ceil(endDateRemaining / 86400)}d {Math.floor((endDateRemaining % 86400) / 3600)}h
              </span>
            </div>
          </div>
        )}

        {/* Helper text about bonus availability */}
        <div className="text-center mx-4 mb-2">
          <div className="text-gray-400 dark:text-gray-500 text-xs">
            Daily bonuses are available for 7 days after signup or each purchase.
          </div>
        </div>

      </div>
    </div>
  );
};

export default DailyRewardModal;
